import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { AuthClientConfig, isHttpInterceptorRouteConfig } from '@auth0/auth0-angular';
import { FwDialogService } from '@flywheel-io/vision';
import * as Sentry from '@sentry/angular';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { CachingService } from './caching.service';
import { CoreApiService, queryAll } from './core-api.service';
import { ErrorDialog } from './dialogs/error-dialog/error-dialog.component';
import { createCachedResource } from './shared/caching.utils';
import { RequestState } from './shared/enums/request-state.enum';
import {
  ApiConfig,
  AuthConfig,
  CentralConfig,
  DeleteReasons,
  ProjectLockingReasons,
} from './shared/models/api-config.model';
import { FeatureFlag } from './shared/models/feature-flags.model';
import { FileTypes } from './shared/models/file.model';
import { ProjectSharingFilterOptions } from './shared/models/project.model';
import { Provider } from './shared/models/provider.model';
import { SiteSettings } from './shared/models/site-settings.model';
import { Version } from './shared/models/version.model';
import { queryStringToParams } from './shared/url.utils';
import { isVersionAtLeast } from './shared/version.utils';
import { loadGoogleTagManager } from './vendor/gtag';
import { getError, initializeSentry } from './vendor/sentry';
import { SSE, SSEOptions } from './vendor/sse';
import { initializeUserpilot } from './vendor/userpilot';

type Features = {
  [key in FeatureFlag]?: boolean | string;
};

/**
 * These are features that don't require explicit configuration in order to be enabled.
 */
const standardFeatures: Features = {
  auth0: true,
  google_analytics: true,
  sentry_logging: false,
  userpilot: true,
};

@Injectable({
  providedIn: 'root',
})
export class ConfigService extends CachingService {
  private httpBackend = inject(HttpBackend);
  private http = new HttpClient(this.httpBackend); // for making requests that bypass configured interceptors

  private authClientConfig = inject(AuthClientConfig);
  private dialog = inject(FwDialogService);
  private coreApi = inject(CoreApiService);

  constructor() {
    super();
  }

  public apiConfig: ApiConfig;
  public features: Features = {};
  public wipFeatures: Features = {};
  public authAudience: string;

  private authConfigSignal: WritableSignal<AuthConfig | null> = signal(null);
  private authConfigFetched = false;
  readonly authConfig = this.authConfigSignal.asReadonly();

  private deleteReasons = {
    state: RequestState.Empty,
    subject: new ReplaySubject<DeleteReasons>(1),
    request: this.coreApi.config.deleteReasons.get(),
  };
  readonly deleteReasons$ = this.deleteReasons.subject.asObservable();

  private fileTypes = createCachedResource(this.coreApi.config.fileTypes.get());

  private projectLockingReasons = {
    state: RequestState.Empty,
    subject: new ReplaySubject<ProjectLockingReasons>(1),
    request: this.coreApi.config.projectLockingReasons.get(),
  };
  readonly projectLockingReasons$ = this.projectLockingReasons.subject.asObservable();

  private projectSharingFilterOptions = createCachedResource(this.coreApi.config.projectSharingFilterOptions.get());

  private providers = {
    state: RequestState.Empty,
    subject: new ReplaySubject<Provider[]>(1),
    request: queryAll(this.coreApi.site.providers.query),
  };
  readonly providers$ = this.providers.subject.asObservable();

  private siteSettings = {
    state: RequestState.Empty,
    subject: new ReplaySubject<SiteSettings>(1),
    request: this.coreApi.site.settings.get(),
  };
  readonly siteSettings$ = this.siteSettings.subject.asObservable();

  private version = {
    state: RequestState.Empty,
    subject: new ReplaySubject<Version>(1),
    request: this.coreApi.version(),
  };
  readonly version$ = this.version.subject.asObservable();

  private apiConfigPromise: Promise<ApiConfig> | null = null;
  private apiConfigLoaded = new ReplaySubject<ApiConfig>(1);
  /**
   * Emits once the api config (and its feature flags) has loaded, without triggering the load.
   * Anything requested before the root route's resolver runs can wait on this instead of reading unset flags.
   */
  readonly apiConfig$ = this.apiConfigLoaded.asObservable();

  /**
   * Load the config object from the API and initialize properties on this service.
   * This requires an authenticated request, so it's called from the auth-guarded route's
   * resolver rather than an app initializer. Subsequent calls return the same promise
   * unless the previous load failed.
   */
  loadApiConfig(): Promise<ApiConfig> {
    if (!this.apiConfigPromise) {
      this.apiConfigPromise = this.fetchApiConfig().catch((error) => {
        this.apiConfigPromise = null;
        throw error;
      });
    }
    return this.apiConfigPromise;
  }

  private fetchApiConfig(): Promise<ApiConfig> {
    return firstValueFrom(this.coreApi.config.get())
      .catch((error) => {
        /* eslint-disable-next-line no-console */
        console.error('Error while fetching site config ', error);
        // Sentry.captureException(getError(apiConfig.error));
        this.showErrorDialog();
        throw error;
      })
      .then((apiConfig) => {
        this.apiConfig = apiConfig;
        this.updateFeatures();
        this.addCentralToAuthAllowedList();
        this.wipFeatures = Object.assign({}, apiConfig.wip_features);

        const sentryEnabled = this.isFeatureEnabled('sentry_logging');
        if (sentryEnabled) {
          initializeSentry();
        }

        if (apiConfig.error) {
          console.error('Error while fetching site config ', apiConfig.error); // eslint-disable-line no-console
          this.showErrorDialog();
          if (sentryEnabled) {
            Sentry.captureException(getError(apiConfig.error));
          }
        }

        if (this.isFeatureEnabled('google_analytics')) {
          const measurementId = this.getFeatureSetting('google_analytics');
          loadGoogleTagManager(typeof measurementId === 'string' ? measurementId : undefined);
        }
        if (this.isFeatureEnabled('userpilot')) {
          initializeUserpilot();
        }
        this.apiConfigLoaded.next(apiConfig);
        return apiConfig;
      });
  }

  /**
   * The central manager url comes from the api config, which isn't available until after
   * auth0 has been configured, so add it to the auth0 interceptor's allowed list here.
   * The interceptor reads its config on every request, so this takes effect immediately.
   */
  private addCentralToAuthAllowedList(): void {
    const managerUrl = this.apiConfig.central?.manager_url;
    const authConfig = this.authClientConfig.get();
    const allowedList = authConfig?.httpInterceptor?.allowedList;
    // no allowed list means auth0 is disabled
    if (!managerUrl || !allowedList) {
      return;
    }
    const uri = managerUrl + '*';
    const alreadyAllowed = allowedList.some(
      (route) => (isHttpInterceptorRouteConfig(route) ? route.uri : route) === uri,
    );
    if (alreadyAllowed) {
      return;
    }
    this.authClientConfig.set({
      ...authConfig,
      httpInterceptor: {
        ...authConfig.httpInterceptor,
        allowedList: [...allowedList, { uri }],
      },
    });
  }

  async loadAuthConfig(): Promise<AuthConfig | null> {
    try {
      if (this.authConfigFetched) {
        return this.authConfigSignal();
      }
      // Uses an independent http client to make request without auth.
      const response = await firstValueFrom(this.http.get<AuthConfig>('/api/config/auth'));
      const authConfig: AuthConfig = response;
      this.authConfigSignal.set(authConfig);
      this.updateFeatures();
      this.authConfigFetched = true;
      return authConfig;
    } catch (error) {
      if (error?.status === 404) {
        // we expect a 404 on sites without auth0 configured
        return null;
      }
      /* eslint-disable-next-line no-console */
      console.error('Error while fetching auth config ', error);
      return null;
    }
  }

  private updateFeatures(): void {
    // Standard Features <- API Config <- Query Parameters
    this.features = {
      ...standardFeatures,
      ...(this.apiConfig?.features || {}),
      ...{ auth: this.authConfigSignal() },
      ...queryStringToParams(window.location.search, coerceBoolean),
      ...this.getLocalStorageFeatureOverrides(),
    };
  }

  getLocalStorageFeatureOverrides(): Features {
    const overridesString = localStorage.getItem('flywheelFeatureOverrides');
    try {
      const parsedOverrides = JSON.parse(overridesString);
      return parsedOverrides || {};
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error(`Error parsing feature overrides from local storage: ${(err as SyntaxError)?.message}`);
      return {};
    }
  }

  getApiUrl(url: string): string {
    return (this.apiConfig?.api_base ?? '') + url;
  }

  getFeatureSetting(name: FeatureFlag): boolean | string {
    return this.features[name];
  }

  getInProgressFeatureSetting(name: string): boolean | string {
    return this.wipFeatures[name];
  }

  isFeatureEnabled(name: FeatureFlag): boolean {
    return Boolean(this.getFeatureSetting(name));
  }

  isInProgressFeatureEnabled(name: string): boolean {
    return Boolean(this.getInProgressFeatureSetting(name));
  }

  getEnabledFeatures(): FeatureFlag[] {
    return Object.entries(this.features)
      .filter(([, value]) => Boolean(value))
      .map(([key]) => key) as FeatureFlag[];
  }

  getCentralConfig(): CentralConfig {
    // can be called from guards that run before the api config loads
    return this.apiConfig?.central;
  }

  getDeleteReasons({ refresh = false } = {}): Observable<DeleteReasons> {
    return this.getCacheableResource(this.deleteReasons, this.deleteReasons$, { refresh });
  }

  getProjectLockingReasons({ refresh = false } = {}): Observable<ProjectLockingReasons> {
    return this.getCacheableResource(this.projectLockingReasons, this.projectLockingReasons$, { refresh });
  }

  getProjectSharingFilterOptions({ refresh = false } = {}): Observable<ProjectSharingFilterOptions> {
    if (refresh) {
      this.projectSharingFilterOptions.refresh$.next();
    }
    return this.projectSharingFilterOptions.result$;
  }

  getProviders({ refresh = false } = {}): Observable<Provider[]> {
    return this.getCacheableResource(this.providers, this.providers$, { refresh });
  }

  getSiteSettings({ refresh = false } = {}): Observable<SiteSettings> {
    return this.getCacheableResource(this.siteSettings, this.siteSettings$, { refresh });
  }

  getVersion({ refresh = false } = {}): Observable<Version> {
    return this.getCacheableResource(this.version, this.version$, { refresh });
  }

  getFileTypes({ refresh = false } = {}): Observable<FileTypes> {
    if (refresh) {
      this.fileTypes.refresh$.next();
    }
    return this.fileTypes.result$;
  }

  setSiteSettings(body: Partial<SiteSettings>): Observable<SiteSettings> {
    const observable = this.coreApi.site.settings.put(body).pipe(
      tap((updatedSiteSettings) => this.siteSettings.subject.next(updatedSiteSettings)),
      shareReplay(1),
    );
    return observable;
  }

  /**
   * Use to check if flywheel_release version is at least the targetVersion or greater.
   * @param {string} targetVersion - version to check against i.e. '15.6.0'
   * @returns {Observable<boolean>} - assumes true if flywheel_release cannot be parsed; otherwise true if version is >= targetVersion
   */
  isVersionAtLeast(targetVersion: string): Observable<boolean> {
    return this.getVersion().pipe(map((version) => isVersionAtLeast(version.flywheel_release, targetVersion)));
  }

  createSSE(url: string, options: SSEOptions): SSE {
    return new SSE(url, options);
  }

  // app.module.ts should call `loadAuthConfig` before checking if Auth0 is enabled.
  // its not safe to make this async as routers rely on it
  isAuth0Enabled(): boolean {
    // api config isn't available yet, but these could come from the search hash or local storage, which gets loaded with auth config
    if (this.features.auth0 === false || this.features.auth0 === 'false') {
      return false;
    }
    const authConfig = this.authConfigSignal();
    if (!authConfig?.auth0 || (authConfig?.auth0?.audience?.length || 0) < 1) {
      return false;
    }
    return true;
  }

  isAssistantChatEnabled(): boolean {
    return this.apiConfig?.assistant_chat?.enabled ?? false;
  }

  getAssistantChatUrl(): string | null {
    return this.apiConfig?.assistant_chat?.url || null;
  }

  private showErrorDialog(): void {
    this.dialog.open(ErrorDialog, {
      data: {
        contentText:
          'Configuration could not be retrieved. Reload the page or contact an administrator if the problem persists.',
        title: 'Configuration Error',
        cancelButtonText: 'Close',
      },
    });
  }
}

function coerceBoolean(value = ''): boolean | string {
  switch (value.toLowerCase()) {
    case 'true':
      return true;
    case 'false':
      return false;
    default:
      return value;
  }
}
